import Phaser from 'phaser';
import type { EnemyId } from './enemies';
import {addFrameMotion} from './frameMotion';
import {REGIONAL_BOSSES} from '../../../shared/regionalBosses.mjs';

export type NamedGhostVisualMeta = {
  hp?:number;
  maxHp?:number;
  archetypeId?:string;
  displayName?:string;
  visualIndex?:number;
  headSignature?:string;
};

export type GhostVisual = {
  container: Phaser.GameObjects.Container;
  hitTarget: Phaser.GameObjects.GameObject;
  bossAura?: Phaser.GameObjects.Arc;
  animate?: (seconds:number,time:number,speed:number,attacking:boolean)=>void;
};

function image(
  scene: Phaser.Scene,
  key: string,
  x: number,
  y: number,
  width: number,
  height: number,
  tint?: number,
): Phaser.GameObjects.Image {
  const node = scene.add.image(x, y, key).setDisplaySize(width, height);
  if (tint !== undefined) node.setTint(tint);
  return node;
}

function leaf(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  angle: number,
  color: number,
  alpha = 1,
): Phaser.GameObjects.Ellipse {
  return scene.add.ellipse(x, y, width, height, color, alpha).setAngle(angle);
}

function makeGeneratedGhost(scene: Phaser.Scene, id: EnemyId): GhostVisual {
  const config = {
    krahang: {key:'gen-ghost-krahang',height:160,y:-32,glow:0x967b53},
    'violent-dead': {key:'gen-ghost-violent-dead',height:150,y:-28,glow:0x9c738d},
    pop: { key: 'gen-ghost-pop', height: 132, y: -18, glow: 0x967358 },
    krasue: { key: 'gen-ghost-krasue', height: 132, y: -25, glow: 0xb93f48 },
    tanee: { key: 'wr-ghost-lup-hua', height: 132, y: -66, glow: 0x64856a },
    'mae-tanee': { key: 'gen-ghost-mae-tanee', height: 200, y: -45, glow: 0x8ca060 },
  }[id];
  const shadow=scene.add.ellipse(0,0,id==='mae-tanee'?92:62,13,0x010307,.48);
  const aura=scene.add.circle(0,-config.height*.5,id==='mae-tanee'?76:48,config.glow,id==='mae-tanee'?.10:.045).setBlendMode(Phaser.BlendModes.ADD);
  const sprite=scene.add.image(0,0,config.key).setOrigin(.5,1).setFlipX(true);
  sprite.setScale(config.height/sprite.height);
  const hit=scene.add.rectangle(0,-config.height*.5,sprite.displayWidth*.65,config.height*.72,0xffffff,.001);
  const container=scene.add.container(0,0,[shadow,aura,sprite,hit]);
  container.setData('ghost-id',id);
  const frames=addFrameMotion(scene,sprite);
  return {container,hitTarget:hit,animate:(_seconds,time,speed,attacking)=>frames(time,speed,attacking),...(id==='mae-tanee'?{bossAura:aura}:{})};
}

function makePop(scene: Phaser.Scene): GhostVisual {
  const shadow = scene.add.ellipse(0, 40, 72, 16, 0x081019, 0.42);
  const leftLeg = image(scene, 'ghost-leg-green', -17, 30, 24, 58, 0x718a5e).setAngle(8);
  const rightLeg = image(scene, 'ghost-leg-green', 17, 30, 24, 58, 0x718a5e).setFlipX(true).setAngle(-8);
  const leftArm = image(scene, 'ghost-arm-green', -37, 0, 24, 78, 0x668057).setAngle(22);
  const rightArm = image(scene, 'ghost-arm-green', 37, 0, 24, 78, 0x668057).setFlipX(true).setAngle(-22);
  const body = image(scene, 'ghost-body-green', 0, -3, 78, 104, 0x71895f);
  const leftEye = image(scene, 'ghost-eye-yellow', -15, -21, 18, 18, 0xeacb5c);
  const rightEye = image(scene, 'ghost-eye-yellow', 15, -21, 18, 18, 0xeacb5c).setFlipX(true);
  const mouth = image(scene, 'ghost-mouth-fangs', 0, 6, 36, 14, 0xe6dbc4);
  const hungerMark = scene.add.arc(0, 25, 27, 18, 162, false, 0x321e20, 0.35);
  const container = scene.add.container(0, 0, [shadow, leftLeg, rightLeg, leftArm, rightArm, body, leftEye, rightEye, mouth, hungerMark]);
  container.setData('ghost-id', 'pop');
  return { container, hitTarget: body };
}

function makeKrasue(scene: Phaser.Scene): GhostVisual {
  const aura = scene.add.circle(0, -16, 51, 0xd45454, 0.12);
  const hairBack = scene.add.ellipse(0, -20, 92, 94, 0x17151d, 0.96);
  const face = image(scene, 'ghost-body-white', 0, -22, 74, 74, 0xbca79e);
  const eyeLeft = image(scene, 'ghost-eye-red', -15, -29, 18, 16, 0xff6a63);
  const eyeRight = image(scene, 'ghost-eye-red', 15, -29, 18, 16, 0xff6a63).setFlipX(true);
  const mouth = image(scene, 'ghost-mouth-sad', 0, -8, 30, 11, 0x4a2328);
  const hairFrontL = scene.add.rectangle(-31, -9, 19, 76, 0x191720, 1).setAngle(10);
  const hairFrontR = scene.add.rectangle(31, -9, 19, 76, 0x191720, 1).setAngle(-10);
  const core = scene.add.circle(0, 25, 14, 0xd7564d, 0.88).setStrokeStyle(3, 0xffb079, 0.55);
  const entrails = scene.add.graphics();
  entrails.lineStyle(5, 0x9e454d, 0.9);
  for (const dx of [-22, -10, 2, 14, 24]) {
    entrails.beginPath();
    entrails.moveTo(dx * 0.4, 30);
    entrails.lineTo(dx, 56);
    entrails.lineTo(dx * 0.55, 83 + Math.abs(dx) * 0.28);
    entrails.strokePath();
  }
  entrails.lineStyle(2, 0xe8a27d, 0.45);
  entrails.strokeCircle(0, 26, 18);
  const container = scene.add.container(0, -10, [aura, entrails, core, hairBack, face, eyeLeft, eyeRight, mouth, hairFrontL, hairFrontR]);
  container.setData('ghost-id', 'krasue');
  return { container, hitTarget: face };
}

function makeTanee(scene: Phaser.Scene): GhostVisual {
  const shadow = scene.add.ellipse(0, 52, 66, 14, 0x081019, 0.34);
  const hairBack = scene.add.ellipse(0, -27, 76, 128, 0x15191a, 0.97);
  const body = image(scene, 'ghost-body-green', 0, -3, 60, 106, 0x8aa376);
  const leftArm = image(scene, 'ghost-arm-green', -34, 1, 20, 86, 0x8ba776).setAngle(10);
  const rightArm = image(scene, 'ghost-arm-green', 34, 1, 20, 86, 0x8ba776).setFlipX(true).setAngle(-10);
  const eyeLeft = image(scene, 'ghost-eye-human', -11, -31, 13, 13, 0xd3ddc9);
  const eyeRight = image(scene, 'ghost-eye-human', 11, -31, 13, 13, 0xd3ddc9).setFlipX(true);
  const skirtLeaves = [
    leaf(scene, -22, 37, 45, 17, 72, 0x55744b),
    leaf(scene, -8, 43, 53, 18, 83, 0x638556),
    leaf(scene, 8, 43, 53, 18, 97, 0x5a7c50),
    leaf(scene, 22, 37, 45, 17, 108, 0x55744b),
  ];
  const hairFront = scene.add.rectangle(-25, -14, 12, 78, 0x15191a, 1).setAngle(5);
  const sash = scene.add.rectangle(0, 13, 50, 7, 0xb6a35f, 0.78).setAngle(-5);
  const container = scene.add.container(0, 0, [shadow, hairBack, ...skirtLeaves, leftArm, rightArm, body, eyeLeft, eyeRight, hairFront, sash]);
  container.setData('ghost-id', 'tanee');
  return { container, hitTarget: body };
}

function makeMaeTanee(scene: Phaser.Scene): GhostVisual {
  const aura = scene.add.circle(0, -20, 92, 0x7bb26a, 0.10).setStrokeStyle(3, 0xc7db83, 0.23);
  const haloLeaves: Phaser.GameObjects.Ellipse[] = [];
  for (let index = 0; index < 12; index += 1) {
    const angle = index * 30;
    const radians = Phaser.Math.DegToRad(angle);
    const x = Math.cos(radians) * 64;
    const y = -32 + Math.sin(radians) * 54;
    haloLeaves.push(leaf(scene, x, y, 82, 19, angle, index % 2 === 0 ? 0x5a814f : 0x6b925a, 0.62));
  }
  const vineBack = scene.add.graphics();
  vineBack.lineStyle(8, 0x496b43, 0.78);
  for (const side of [-1, 1]) {
    vineBack.beginPath();
    vineBack.moveTo(side * 26, 20);
    vineBack.lineTo(side * 62, 48);
    vineBack.lineTo(side * 88, 28);
    vineBack.lineTo(side * 110, 58);
    vineBack.strokePath();
  }
  const hairBack = scene.add.ellipse(0, -30, 92, 154, 0x111719, 0.99);
  const body = image(scene, 'ghost-body-dark', 0, -1, 76, 126, 0x5f7259);
  const leftArm = image(scene, 'ghost-arm-dark', -50, 2, 29, 105, 0x566a52).setAngle(18);
  const rightArm = image(scene, 'ghost-arm-dark', 50, 2, 29, 105, 0x566a52).setFlipX(true).setAngle(-18);
  const eyeLeft = image(scene, 'ghost-eye-red', -14, -42, 16, 14, 0xf1746f);
  const eyeRight = image(scene, 'ghost-eye-red', 14, -42, 16, 14, 0xf1746f).setFlipX(true);
  const crownLeaves = [
    leaf(scene, -30, -100, 76, 17, -55, 0x789c5f),
    leaf(scene, -12, -112, 84, 18, -78, 0x83a866),
    leaf(scene, 12, -112, 84, 18, 78, 0x83a866),
    leaf(scene, 30, -100, 76, 17, 55, 0x789c5f),
  ];
  const skirtLeaves = [
    leaf(scene, -28, 47, 70, 22, 70, 0x486b43),
    leaf(scene, -9, 56, 78, 23, 84, 0x567a4a),
    leaf(scene, 9, 56, 78, 23, 96, 0x567a4a),
    leaf(scene, 28, 47, 70, 22, 110, 0x486b43),
  ];
  const amulet = scene.add.rectangle(0, -2, 18, 18, 0xd5b45e, 0.92).setAngle(45).setStrokeStyle(2, 0xffe7a4, 0.55);
  const container = scene.add.container(0, -12, [aura, ...haloLeaves, vineBack, hairBack, ...skirtLeaves, leftArm, rightArm, body, ...crownLeaves, eyeLeft, eyeRight, amulet]);
  container.setData('ghost-id', 'mae-tanee');
  return { container, hitTarget: body, bossAura: aura };
}

function makeNamedFemaleGhost(scene:Phaser.Scene,id:EnemyId,meta:NamedGhostVisualMeta):GhostVisual {
  const index=Math.max(0,Math.min(9,meta.visualIndex??0));
  const palettes=[
    {aura:0x6ec5a2,tint:0xddebd9,mark:'✿'},
    {aura:0x6789d8,tint:0xd9e1f4,mark:'❀'},
    {aura:0xbc593f,tint:0xf1c5ad,mark:'⌁'},
    {aura:0xd94c51,tint:0xf0c0c6,mark:'✦'},
    {aura:0x9e6ddd,tint:0xe2d4f4,mark:'〰'},
    {aura:0x519f79,tint:0xcde5d7,mark:'◉'},
    {aura:0xc4cad8,tint:0xe9e9ef,mark:'◇'},
    {aura:0xd48532,tint:0xf0cf91,mark:'✧'},
    {aura:0xc39a45,tint:0xead6a4,mark:'⚶'},
    {aura:0xe6d39b,tint:0xf4ead2,mark:'☁'},
  ][index]!;
  const shadow=scene.add.ellipse(0,0,74,14,0x010307,.38);
  const aura=scene.add.circle(0,-78,62,palettes.aura,.10).setStrokeStyle(2,palettes.aura,.28).setBlendMode(Phaser.BlendModes.ADD);
  const veil=scene.add.ellipse(index%2===0?-20:20,-78,82,152,palettes.aura,.07).setAngle(index%2===0?-8:8);
  const sprite=scene.add.image(0,0,'wr-ghost-'+meta.archetypeId).setOrigin(.5,1);
  const safeHeight=164;
  sprite.setScale(safeHeight/Math.max(1,sprite.height));
  const frames=addFrameMotion(scene,sprite,index*137);
  const ornament=scene.add.text(index%3===0?-19:index%3===1?0:18,-164,palettes.mark,{
    fontFamily:'Noto Sans Thai, system-ui',
    fontSize:'24px',
    color:'#ffe4a8',
    stroke:'#28170e',
    strokeThickness:4,
  }).setOrigin(.5);
  const label=scene.add.text(0,scene.scale.height<560?-156:-188,meta.displayName??'ผีสาว',{
    fontFamily:'Noto Sans Thai, system-ui',
    fontSize:'13px',
    fontStyle:'bold',
    color:'#fff0cf',
    backgroundColor:'rgba(12,12,19,.78)',
    stroke:'#23140e',
    strokeThickness:3,
    padding:{x:6,y:3},
  }).setOrigin(.5,1);
  const hit=scene.add.rectangle(0,-80,Math.max(42,sprite.displayWidth*.58),safeHeight*.76,0xffffff,.001);
  const container=scene.add.container(0,0,[shadow,aura,veil,sprite,ornament,label,hit]);
  container.setData('ghost-id',id);
  container.setData('archetype-id',meta.archetypeId??'');
  container.setData('display-name',meta.displayName??'');
  container.setData('head-signature',meta.headSignature??'');
  return {
    container,
    hitTarget:hit,
    animate:(seconds,time,speed,attacking)=>{
      frames(time,speed,attacking);
      label.setScale(container.scaleX<0?-1:1,1);
      const live=container.getData('snapshot') as NamedGhostVisualMeta|undefined;
      label.setText('ศัตรู · '+(live?.displayName??meta.displayName??'ผีสาว')+(live?.hp!==undefined?'\nHP '+live.hp+'/'+live.maxHp:''));
      const sway=Math.sin(time/430+index*.7);
      veil.rotation=sway*.045;
      ornament.y=-164+Math.sin(time/280+index)*2;
      aura.alpha=attacking?.18:.08+Math.sin(time/500+index)*.025;
      void seconds;void speed;
    },
  };
}

export function createGhostVisual(scene: Phaser.Scene, id: EnemyId, meta?:NamedGhostVisualMeta): GhostVisual {
  if(meta?.archetypeId?.startsWith('ow-')){
    const shadow=scene.add.ellipse(0,0,74,14,0x010307,.4),sprite=scene.add.image(0,0,'ow-ghost-'+meta.archetypeId.slice(3)).setOrigin(.5,1);
    sprite.setScale(190/sprite.height);
    const frames=addFrameMotion(scene,sprite);
    const label=scene.add.text(0,-207,'BOSS · '+(meta.displayName??'ผู้ถือทะเบียน')+'\n'+(REGIONAL_BOSSES[meta.archetypeId]?.hint??''),{fontFamily:'Noto Sans Thai',fontSize:'14px',align:'center',color:'#ffe9af',stroke:'#090c11',strokeThickness:4}).setOrigin(.5);
    const aura=scene.add.circle(0,-80,75,0xc19452,.08).setStrokeStyle(2,0xcc9d53,.25);
    const container=scene.add.container(0,0,[shadow,aura,sprite,label]),hit=scene.add.rectangle(0,-88,68,165,0xffffff,0);container.add(hit);
    return {container,hitTarget:hit,bossAura:aura,animate:(_seconds,time,speed,attacking)=>{frames(time,speed,attacking);aura.alpha=attacking?.17:.07;}};
  }
  if(meta?.archetypeId)return makeNamedFemaleGhost(scene,id,meta);
  return makeGeneratedGhost(scene, id);
}
